import React, { useEffect, useContext, useState } from "react";
import { Button, Stack } from "@mui/material";
import Api from "api";
import strings from "localization/strings";
import { ErrorContext } from "components/contexts/error-handler";
import { useApiClient, useAppDispatch } from "app/hooks";
import { Metaform, MetaformMember, MetaformMemberGroup, User } from "generated/client";
import AddMemberGroupDialog from "components/users/add-member-group-dialog";
import UsersTable from "components/users/users-table";
import UsersFilter from "components/users/users-filter";
import { setSnackbarMessage } from "features/snackbar-slice";
import AddMemberDialog from "components/users/add-member-dialog";
import EditMemberDialog from "components/users/edit-member-dialog";

/**
 * Users screen component
 */
const UsersScreen: React.FC = () => {
  const errorContext = useContext(ErrorContext);
  const apiClient = useApiClient(Api.getApiClient);
  const { metaformsApi, metaformMemberGroupsApi, metaformMembersApi, usersApi } = apiClient;

  const dispatch = useAppDispatch();

  const [ loading, setLoading ] = useState<boolean>(false);
  const [ loadingMemberId, setLoadingMemberId ] = useState<string>();
  const [ metaforms, setMetaforms ] = useState<Metaform[]>([]);
  const [ memberGroups, setMemberGroups ] = useState<MetaformMemberGroup[]>([]);
  const [ members, setMembers ] = useState<MetaformMember[]>([]);
  const [ selectedMetaformId, setSelectedMetaformId ] = useState<string>();
  const [ selectedMemberGroupId, setSelectedMemberGroupId ] = useState<string>();
  const [ addMemberGroupOpen, setAddMemberGroupOpen ] = useState<boolean>(false);
  const [ addMemberOpen, setAddMemberOpen ] = useState<boolean>(false);
  const [ editMemberOpen, setEditMemberOpen ] = useState<boolean>(false);

  /**
   * Searches users from the API
   * 
   * @param search search
   */
  const searchUsers = async (search: string): Promise<User[]> => {
    setLoading(true);

    try {
      const users = await usersApi.listUsers({ search: search });
      
      return users;
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.loadUsers, err);
    }

    setLoading(false);

    return [];
  };

  /**
   * Creates new User
   * 
   * @param user user
   */
  const createUser = async (user: User): Promise<User | undefined> => {
    setLoading(true);
    
    try {
      const createdUser = await usersApi.createUser({ user: user });

      return createdUser;
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.createUser, err);
    }

    setLoading(false);
  };

  /**
   * Edits User
   * 
   * @param user User
   */
  const editUser = async (user: User) => {
    setLoading(true);

    try {
      await usersApi.updateUser({
        userId: user.id!,
        user: user
      });

      dispatch(setSnackbarMessage(strings.successSnackbars.users.editUserSuccessText));
    } catch (e) {
      errorContext.setError(strings.errorHandling.usersScreen.updateUser, e);
    }
  };

  /**
   * Load metaforms from the API
   */
  const loadMetaforms = async () => {
    try {
      setLoading(true);
      setMetaforms(await metaformsApi.listMetaforms({ }));
      setLoading(false);
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.loadMetaforms, err);
    }
  };

  /**
   * Load metaform member groups from the API
   */
  const loadMemberGroups = async () => {
    if (!selectedMetaformId) {
      setMemberGroups([]);
      return;
    }

    try {
      setMemberGroups(await metaformMemberGroupsApi.listMetaformMemberGroups({
        metaformId: selectedMetaformId
      }));
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.loadMemberGroups, err);
    }
  };

  /**
   * Load metaform members from the API
   */
  const loadMetaformMembers = async () => {
    if (!selectedMetaformId) {
      setMembers([]);
      return;
    }

    try {
      setMembers(await metaformMembersApi.listMetaformMembers({
        metaformId: selectedMetaformId
      }));
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.loadMembers, err);
    }
  };

  /**
   * Loads members and groups
   */
  const loadMembersAndGroups = async () => {
    setLoading(true);

    await Promise.all([
      loadMemberGroups(),
      loadMetaformMembers()
    ]);

    setLoading(false);
  };

  /**
   * Event handler for group membership removal from member
   *
   * @param metaformMember metaform member
   * @param groupId group id
   */
  const onGroupMembershipRemove = async (metaformMember: MetaformMember, groupId: string) => {
    if (!selectedMetaformId) {
      return;
    }

    setLoadingMemberId(metaformMember.id);

    try {
      const memberGroup = memberGroups.find(metaformMemberGroup => metaformMemberGroup.id === groupId);
      if (!memberGroup) {
        errorContext.setError(strings.errorHandling.usersScreen.removeMemberNotFound);
        return;
      }

      const updatedGroup = await metaformMemberGroupsApi.updateMetaformMemberGroup({
        metaformId: selectedMetaformId,
        metaformMemberGroupId: groupId,
        metaformMemberGroup: { ...memberGroup, memberIds: memberGroup.memberIds.filter(memberId => memberId !== metaformMember.id) }
      });

      const updatedGroups = memberGroups.map(metaformMemberGroup => (metaformMemberGroup.id === updatedGroup.id ? updatedGroup : metaformMemberGroup));

      dispatch(setSnackbarMessage(strings.successSnackbars.users.groupMembershipRemoveSuccessText));
      setMemberGroups(updatedGroups);
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.loadMembers, err);
    }

    setLoadingMemberId(undefined);
  };

  /**
   * Event handler for group membership add to member
   *
   * @param metaformMember metaform member
   * @param groupId group id
   */
  const onGroupMembershipAdd = async (metaformMember: MetaformMember, groupId: string) => {
    if (!selectedMetaformId) {
      return;
    }

    setLoadingMemberId(metaformMember.id);

    try {
      const memberGroup = memberGroups.find(metaformMemberGroup => metaformMemberGroup.id === groupId);
      if (!memberGroup) {
        errorContext.setError(strings.errorHandling.usersScreen.addMemberNotFound);
        return;
      }

      const updatedGroup = await metaformMemberGroupsApi.updateMetaformMemberGroup({
        metaformId: selectedMetaformId,
        metaformMemberGroupId: groupId,
        metaformMemberGroup: { ...memberGroup, memberIds: [ ...memberGroup.memberIds, metaformMember.id! ] }
      });

      const updatedGroups = memberGroups.map(metaformMemberGroup => (metaformMemberGroup.id === updatedGroup.id ? updatedGroup : metaformMemberGroup));

      dispatch(setSnackbarMessage(strings.successSnackbars.users.groupMembershipAddSuccessText));
      setMemberGroups(updatedGroups);
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.loadMembers, err);
    }

    setLoadingMemberId(undefined);
  };

  /**
   * Event handler for member group dialog create
   *
   * @param displayName group's display name
   */
  const onAddMemberGroupDialogCreate = async (displayName: string | undefined) => {
    if (!selectedMetaformId || !displayName) {
      return;
    }

    setLoading(true);

    try {
      const createdMemberGroup = await metaformMemberGroupsApi.createMetaformMemberGroup({
        metaformId: selectedMetaformId,
        metaformMemberGroup: {
          memberIds: [],
          displayName: displayName
        }
      });

      dispatch(setSnackbarMessage(strings.successSnackbars.users.addMemberGroupSuccessText));
      setMemberGroups([ ...memberGroups, createdMemberGroup ]);
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.createMemberGroup, err);
    }

    setAddMemberGroupOpen(false);
    setLoading(false);
  };

  /**
   * Event handler for member dialog create
   *
   * @param member member's details
   */
  const onAddMemberDialogCreate = async (member: MetaformMember) => {
    if (!selectedMetaformId) {
      return;
    }

    setLoading(true);

    try {
      const createdMember = await metaformMembersApi.createMetaformMember({
        metaformId: selectedMetaformId,
        metaformMember: member
      });

      dispatch(setSnackbarMessage(strings.successSnackbars.users.addMemberSuccessText));
      setMembers([ ...members, createdMember ]);
    } catch (err) {
      errorContext.setError(strings.errorHandling.usersScreen.createMember, err);
    }

    setAddMemberOpen(false);
    setLoading(false);
  };

  /**
   * New member group button click listener
   */
  const onNewMemberGroupButtonClick = () => setAddMemberGroupOpen(true);

  /**
   * Event handler for member group dialog cancel
   */
  const onAddMemberGroupDialogCancel = () => setAddMemberGroupOpen(false);

  /**
   * New member button click listener
   */
  const onNewMemberButtonClick = () => setAddMemberOpen(true);

  /**
   * Event handler for member dialog cancel
   */
  const onAddMemberDialogCancel = () => setAddMemberOpen(false);

  /**
   * Edit User button click listener
   */
  const onEditMemberButtonClick = () => setEditMemberOpen(true);

  /**
   * Event handler for User edit dialog cancel
   */
  const onEditMemberDialogCancel = () => setEditMemberOpen(false);
  
  useEffect(() => {
    loadMetaforms();
  }, []);

  useEffect(() => {
    loadMembersAndGroups();
  }, [ selectedMetaformId, metaforms ]);

  return (
    <>
      <AddMemberDialog
        loading={ loading }
        open={ addMemberOpen }
        onCreate={ onAddMemberDialogCreate }
        onCancel={ onAddMemberDialogCancel }
        setLoading={ setLoading }
        searchUsers={ searchUsers }
        createUser={ createUser }
      />

      <AddMemberGroupDialog
        loading={ loading }
        open={ addMemberGroupOpen }
        onCreate={ onAddMemberGroupDialogCreate }
        onCancel={ onAddMemberGroupDialogCancel }
      />

      <EditMemberDialog
        loading={ loading }
        open={ editMemberOpen }
        setLoading={ setLoading }
        onCancel={ onEditMemberDialogCancel }
        searchUsers={ searchUsers }
        editUser={ editUser }
      />

      <Stack flex={ 1 } minWidth={ 0 }>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={ 1 }
          useFlexGap
          flexWrap="wrap"
          alignItems={{ xs: "stretch", md: "center" }}
          mb={ 2.25 }
        >
          <UsersFilter
            loading={ loading }
            metaforms={ metaforms }
            memberGroups={ memberGroups }
            selectedMetaformId={ selectedMetaformId }
            selectedMemberGroupId={ selectedMemberGroupId }
            setSelectedMetaformId={ setSelectedMetaformId }
            setSelectedMemberGroupId={ setSelectedMemberGroupId }
          />
          <Stack
            direction="row"
            spacing={ 0.75 }
            flexWrap="wrap"
            useFlexGap
            sx={{ ml: { md: "auto !important" }, flexShrink: 0 }}
          >
            <Button
              variant="outlined"
              onClick={ onEditMemberButtonClick }
              sx={{
                borderColor: "#d9dedb", color: "#4d4d4d", fontSize: 13, fontWeight: 500, px: 1.5, py: 0.75
              }}
            >
              { strings.userManagementScreen.editMemberButton }
            </Button>
            <Button
              variant="contained"
              disabled={ !selectedMetaformId }
              onClick={ onNewMemberButtonClick }
              sx={{
                backgroundColor: "#ED8113",
                color: "#fff",
                fontSize: 13,
                fontWeight: 500,
                px: 1.5,
                py: 0.75,
                "&:hover": { backgroundColor: "#c86b0e" }
              }}
            >
              { strings.userManagementScreen.addMemberButton }
            </Button>
            <Button
              variant="outlined"
              disabled={ !selectedMetaformId }
              onClick={ onNewMemberGroupButtonClick }
              sx={{
                borderColor: "rgba(39, 128, 98, 0.5)", color: "#278062", fontSize: 13, fontWeight: 500, px: 1.5, py: 0.75
              }}
            >
              { strings.userManagementScreen.addMemberGroupButton }
            </Button>
          </Stack>
        </Stack>
        <UsersTable
          loading={ loading }
          loadingMemberId={ loadingMemberId }
          memberGroups={ memberGroups }
          members={ members }
          selectedMetaformId={ selectedMetaformId }
          selectedMemberGroupId={ selectedMemberGroupId }
          onGroupMembershipAdd={ onGroupMembershipAdd }
          onGroupMembershipRemove={ onGroupMembershipRemove }
        />
      </Stack>
    </>
  );
};

export default UsersScreen;